// @noflow
import { useCallback, useState } from 'react';
import styled from 'styled-components';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import CircularProgress from '@material-ui/core/CircularProgress';
import AuthPage from './AuthPage';
import Text from 'common/ui/Text';
import Input from 'common/ui/Input';
import Button from 'common/ui/Button_deprecated';
import { RESET_PASSWORD } from 'modules/Apollo/queries';
import { Colors, TextCss, Spacings } from 'styles';
import { useSetRecoilState } from 'recoil';
import { passwordResetSuccessfulState } from 'config/recoilState';
import { usePasswordValidation } from 'hooks/usePasswordValidation';

const PaddedText = styled(Text)`
  ${TextCss.caption}
  margin-left: ${Spacings.small}rem;
  margin-top: ${Spacings.xxsmall}rem;
  margin-bottom: ${Spacings.xxsmall}rem;
`;

const StyledRequirement = styled.li`
  ${TextCss.caption};
  margin-bottom: ${Spacings.xsmall}rem;
`;

const getRequirementColor = (active) => `
  color: ${active ? Colors.green5 : 'white'};
`;

const RequestPasswordReset = () => {
  const [resetPassword] = useMutation(RESET_PASSWORD);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [
    password,
    setPassword,
    setConfirmPassword,
    passwordValidations,
    isPasswordMatching,
    isDisabled,
  ] = usePasswordValidation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const { userId } = useParams();
  const setPasswordResetSuccessful = useSetRecoilState(passwordResetSuccessfulState);

  const submit = useCallback(
    async (evt) => {
      evt.preventDefault();
      const data = Object.fromEntries(new FormData(evt.target).entries());
      setError(null);
      setLoading(true);
      try {
        await resetPassword({ variables: { token, userId, password: data.password } });
        setPasswordResetSuccessful(true);
        navigate('/login');
      } catch (err) {
        setError(err);
      } finally {
        setLoading(false);
      }
    },
    [resetPassword, token, userId, navigate, setPasswordResetSuccessful]
  );

  return (
    <AuthPage as="form" onSubmit={submit} title="Reset password">
      <Link to="/">
        <img
          src="/logos/logoSide.png"
          alt="Sirona Medical Logo"
          css={`
            width: 6.6rem;
            margin-bottom: ${Spacings.small}rem;
          `}
        />
      </Link>

      <Text variant="body2">
        Thank you for confirming your email address. Please enter your new password.
      </Text>

      <div
        css={`
          margin-top: ${Spacings.large}rem;
          margin-bottom: ${Spacings.xxsmall}rem;
          display: flex;
          flex-direction: column;
          width: 100%;
        `}
      >
        <PaddedText as="label" htmlFor="password" /*css="margin-top: 2rem;"*/>
          Password
        </PaddedText>
        <Input
          css={`
            margin-bottom: ${Spacings.small}rem;
          `}
          id="password"
          type="password"
          name="password"
          onChange={(evt) => setPassword(evt.target.value)}
          fullWidth
          placeholder="Enter your password"
          required
        />

        <ul
          data-testid="password-checks"
          css={`
            margin-top: ${Spacings.small}rem;
            padding-inline-start: 3rem;
          `}
        >
          <StyledRequirement css={getRequirementColor(passwordValidations.hasLength)}>
            Minimum of 8 characters long.
          </StyledRequirement>
          <StyledRequirement css={getRequirementColor(passwordValidations.hasUpperCase)}>
            Contains at least one uppercase letter.
          </StyledRequirement>
          <StyledRequirement css={getRequirementColor(passwordValidations.hasLowerCase)}>
            Contains at least one lowercase letter.
          </StyledRequirement>
          <StyledRequirement css={getRequirementColor(passwordValidations.hasNumber)}>
            Contains at least one number.
          </StyledRequirement>
          <StyledRequirement css={getRequirementColor(passwordValidations.hasSpecialChar)}>
            Contains at least one special character.
          </StyledRequirement>
        </ul>

        <PaddedText
          as="label"
          htmlFor="confirmPassword"
          css={`
            margin-top: ${Spacings.xsmall}rem;
          `}
        >
          Re-enter password
        </PaddedText>
        <Input
          id="confirmPassword"
          type="password"
          name="confirmPassword"
          onChange={(evt) => setConfirmPassword(evt.target.value)}
          pattern={password?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')} // must match other password field
          title="Passwords should match."
          fullWidth
          placeholder="Enter your password"
          required
        />
      </div>

      <div css="height: 2rem;  width: 100%;">
        {!isPasswordMatching && (
          <PaddedText
            css={`
              color: ${Colors.yellow3};
              margin-bottom: ${Spacings.xxsmall}rem;
              margin-left: ${Spacings.medium}rem;
              margin-top: ${Spacings.xsmall}rem;
            `}
          >
            Passwords must match
          </PaddedText>
        )}
      </div>

      {error && (
        <PaddedText
          css={`
            color: ${Colors.red5};
            margin-bottom: ${Spacings.xxsmall};
          `}
        >
          {error.message}
        </PaddedText>
      )}

      {!loading ? (
        <Button
          css={`
            margin-top: ${Spacings.xlarge}rem;
          `}
          role="button"
          type="submit"
          variant="roundedBlue"
          fullWidth
          disabled={isDisabled}
        >
          Reset password
        </Button>
      ) : (
        <CircularProgress />
      )}
    </AuthPage>
  );
};

export default RequestPasswordReset;
